import * as React from 'react';
import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

const popoverTriggerVariants = cva('', {
  variants: {
    variant: {
      default: '',
      hint: 'rounded-sm text-left underline decoration-dotted decoration-muted-foreground/60 underline-offset-4 focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
      status: 'timesheet-duration font-medium',
      value: 'ledger-cell-value',
      event: 'truncate rounded-sm px-1.5 text-muted-foreground hover:text-foreground',
    },
    state: {
      default: '',
      pending: 'border border-dashed',
      ready:
        'border border-dashed border-(--ec-event-color)/50 bg-(--ec-event-color)/8 text-foreground',
      danger: 'border border-dashed border-destructive/70 bg-destructive/10 text-destructive',
    },
  },
  defaultVariants: { variant: 'default', state: 'default' },
});

function PopoverTrigger({
  className,
  variant = 'default',
  state = 'default',
  ...props
}: PopoverPrimitive.Trigger.Props & VariantProps<typeof popoverTriggerVariants>) {
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      className={cn(popoverTriggerVariants({ variant, state }), className)}
      {...props}
    />
  );
}

const popoverContentVariants = cva(
  'z-50 flex w-72 origin-(--transform-origin) flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground smooth-shadow-ring-md outline-hidden duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
  {
    variants: {
      size: {
        default: '',
        roomy: 'p-3',
        card: 'gap-4 p-4',
        tight: 'gap-2',
        compact: 'gap-1! p-2! text-xs',
        flush: 'gap-0 p-0',
      },

      appearance: {
        default: '',
        'field-picker':
          'w-auto min-w-56 p-0 data-handoff:data-ending-style:animate-none data-handoff:data-ending-style:transition-none data-handoff:data-[state=closed]:animate-none data-handoff:data-[state=closed]:transition-none',
      },
    },
    defaultVariants: { size: 'default' },
  },
);

function PopoverContent({
  appearance = 'default',
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  size = 'default',
  centerOnOverflow = false,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset'> &
  VariantProps<typeof popoverContentVariants> & { centerOnOverflow?: boolean }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        ref={centerOnOverflow ? centerClippedPopover : undefined}
        className="isolate z-50 data-centered:fixed! data-centered:top-(--popover-center-y)! data-centered:left-(--popover-center-x)! data-centered:right-auto! data-centered:bottom-auto! data-centered:-translate-x-1/2! data-centered:-translate-y-1/2! data-centered:transform-none!"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(popoverContentVariants({ size, appearance }), className)}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

/** Keep a clipped popup centered until it closes, so resizing cannot make it jump back and forth. */
function centerClippedPopover(positioner: HTMLDivElement | null) {
  if (!positioner) return;
  const viewport = window.visualViewport;
  function place() {
    if (!positioner || positioner.hidden || positioner.style.opacity === '0') return;
    const left = viewport?.offsetLeft ?? 0;
    const top = viewport?.offsetTop ?? 0;
    const width = viewport?.width ?? window.innerWidth;
    const height = viewport?.height ?? window.innerHeight;
    const bounds = positioner.getBoundingClientRect();
    if (
      bounds.left < left ||
      bounds.right > left + width ||
      bounds.top < top ||
      bounds.bottom > top + height
    ) {
      positioner.dataset.centered = '';
    }
    for (const [name, value] of [
      ['--popover-center-x', `${left + width / 2}px`],
      ['--popover-center-y', `${top + height / 2}px`],
      ['--popover-viewport-height', `${Math.max(0, height - 32)}px`],
    ] as const) {
      if (positioner.style.getPropertyValue(name) !== value) {
        positioner.style.setProperty(name, value);
      }
    }
  }
  const size = new ResizeObserver(place);
  const placement = new MutationObserver(place);
  size.observe(positioner);
  placement.observe(positioner, { attributes: true, attributeFilter: ['style', 'hidden'] });
  window.addEventListener('resize', place);
  viewport?.addEventListener('resize', place);
  viewport?.addEventListener('scroll', place);
  place();
  return () => {
    size.disconnect();
    placement.disconnect();
    window.removeEventListener('resize', place);
    viewport?.removeEventListener('resize', place);
    viewport?.removeEventListener('scroll', place);
  };
}

function PopoverHeader({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & { variant?: 'default' | 'tight' }) {
  return (
    <div
      data-slot="popover-header"
      className={cn('flex flex-col text-sm', variant === 'tight' ? 'gap-1' : 'gap-0.5', className)}
      {...props}
    />
  );
}

const popoverTitleVariants = cva('font-medium', {
  variants: {
    variant: {
      default: '',
      filter: 'px-2 text-xs text-muted-foreground',
      xs: 'text-xs',
      heading: 'text-sm font-semibold',
    },
  },
  defaultVariants: { variant: 'default' },
});

function PopoverTitle({
  className,
  variant = 'default',
  ...props
}: PopoverPrimitive.Title.Props & VariantProps<typeof popoverTitleVariants>) {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={cn(popoverTitleVariants({ variant }), className)}
      {...props}
    />
  );
}

function PopoverDescription({
  className,
  size = 'default',
  ...props
}: PopoverPrimitive.Description.Props & { size?: 'default' | 'xs' }) {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={cn('text-muted-foreground', size === 'xs' && 'text-xs', className)}
      {...props}
    />
  );
}

export { Popover, PopoverContent, PopoverDescription, PopoverHeader, PopoverTitle, PopoverTrigger };
